import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pin, Loader2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Container, GoldButton } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

export default function ForumThread() {
  const { id } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/forum/threads/${id}`).then((r) => setThread(r.data)).catch(() => setNotFound(true));
  };
  useEffect(() => { load(); }, [id]);

  const postReply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/forum/threads/${id}/replies`, { body: reply });
      setReply("");
      load();
      toast.success("Reply posted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async () => {
    await api.put(`/admin/forum/threads/${id}`);
    load();
    toast.success("Pin toggled.");
  };
  const removeThread = async () => {
    await api.delete(`/admin/forum/threads/${id}`);
    toast.success("Thread removed.");
    navigate(`/forum/board/${encodeURIComponent(thread.sector)}`);
  };

  if (notFound) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-serif text-3xl text-[#1E1E2F] mb-3">Thread not found</h1>
        <GoldButton as="link" to="/forum">Back to the forum</GoldButton>
      </Container>
    );
  }
  if (!ready || !thread) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;

  const isAdmin = user.role === "admin";

  return (
    <div>
      <section className="bg-[#1E1E2F] text-[#FFFAF3] border-b border-[#D6D3CB]">
        <Container className="py-12">
          <Link to={`/forum/board/${encodeURIComponent(thread.sector)}`} className="inline-flex items-center gap-1 text-[#FFFAF3]/70 hover:text-[#FFFAF3] text-sm mb-4 link-underline">
            <ArrowLeft size={14} /> {thread.sector}
          </Link>
          <h1 className="font-serif text-2xl sm:text-4xl flex items-start gap-3">
            {thread.pinned && <Pin size={22} className="text-[#CDA434] mt-1.5 shrink-0" />}
            {thread.title}
          </h1>
          <p className="font-sans text-sm text-[#FFFAF3]/60 mt-3">{thread.author_name} · {new Date(thread.created_at).toLocaleDateString("en-GB")}</p>
          {isAdmin && (
            <div className="flex gap-3 mt-5">
              <button data-testid="admin-pin-thread" onClick={togglePin} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#CDA434] text-[#CDA434] px-3 py-1.5 hover:bg-[#CDA434] hover:text-[#1E1E2F]">
                <Pin size={13} /> {thread.pinned ? "Unpin" : "Pin"}
              </button>
              <button data-testid="admin-delete-thread" onClick={removeThread} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#FFFAF3]/30 text-[#FFFAF3]/80 px-3 py-1.5 hover:border-[#FFFAF3]">
                <Trash2 size={13} /> Remove
              </button>
            </div>
          )}
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#FFFAF3] border border-[#D6D3CB] p-6 mb-8">
              <p className="font-sans text-base text-[#1B2430] leading-relaxed whitespace-pre-line">{thread.body}</p>
            </div>

            <h2 className="font-serif text-xl text-[#1E1E2F] mb-4">
              {thread.replies.length} repl{thread.replies.length === 1 ? "y" : "ies"}
            </h2>
            <div className="space-y-3 mb-8" data-testid="reply-list">
              {thread.replies.map((r) => (
                <div key={r.id} className="bg-[#FFFAF3] border border-[#D6D3CB] p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-serif text-base text-[#1E1E2F]">{r.author_name}</span>
                    <span className="font-sans text-xs text-[#505A68]">{new Date(r.created_at).toLocaleDateString("en-GB")}</span>
                  </div>
                  <p className="font-sans text-sm text-[#1B2430] leading-relaxed whitespace-pre-line">{r.body}</p>
                </div>
              ))}
            </div>

            {user ? (
              <form onSubmit={postReply} data-testid="reply-form" className="bg-[#FFFAF3] border border-[#D6D3CB] p-6">
                <label className="block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5">Add a reply</label>
                <textarea required rows={3} data-testid="reply-body" value={reply} onChange={(e) => setReply(e.target.value)}
                  className="w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans resize-none mb-4" />
                <GoldButton type="submit" disabled={busy} data-testid="reply-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : "Post reply"}
                </GoldButton>
              </form>
            ) : (
              <div data-testid="reply-signin-prompt" className="bg-[#FFFAF3] border border-[#D6D3CB] p-6 text-center">
                <p className="font-sans text-sm text-[#505A68] mb-4">Sign in to join this conversation.</p>
                <GoldButton as="link" to="/signin">Sign in to reply</GoldButton>
              </div>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
